import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Alert, Badge, Button, Card, Space, Spin, Table, Tag, Typography, message } from 'antd'
import {
  CheckCircleFilled,
  DownOutlined,
  RightOutlined,
  TeamOutlined,
  UserAddOutlined,
  ImportOutlined,
  ApiOutlined,
  FileTextOutlined,
  BankOutlined,
} from '@ant-design/icons'
import fetchAPI from '../../../lib/utils/fetch-api'
import type { CompanyStructure } from './company/types'
import { ImportUsersBlock } from '../../components/v2/import-users-block'
import { ImportOrdersBlock } from '../../components/v2/import-orders-block'
import { InviteEmployeeModal, flattenOfficeOptionsForInvite } from './invite-employee-modal'
import { flattenOfficeOptionsForOrders, flattenOfficeOptionsForUsers } from './import-utils'
import { HomeIntegrationsConnect } from './integrations-connect'
import { hasPermission } from '../../lib/permissions'

const { Text, Title } = Typography

export type OnboardingStepKey = 'office' | 'employees' | 'orders' | 'integrations' | 'templates'

type StepStatus = { key: OnboardingStepKey; done: boolean; skipped: boolean; count?: number }

type OfficeSummary = {
  brand: string | null
  legal_name: string | null
  inn: string | null
  office: string | null
}

type PollSummary = {
  items: Array<{ question: string; answer: string }>
  departments: string[]
}

type EmployeePreview = {
  id: number
  name: string | null
  login: string | null
  roles: string[]
  office: string | null
  is_active: boolean
}

export type OnboardingStatus = {
  steps: StepStatus[]
  office: OfficeSummary
  greeting_name?: string | null
  poll_summary?: PollSummary | null
  employees?: { items: EmployeePreview[]; total: number }
}

const ALL_EMPLOYEES_URL = '/personnel'

type StepMeta = {
  key: OnboardingStepKey
  title: string
  description: string
  icon: React.ReactNode
  /** Ключи меню (allowed_menu_keys): шаг показывается, если доступен хотя бы один */
  menuKeys?: string[]
  skippable: boolean
}

const STEPS: StepMeta[] = [
  {
    key: 'office',
    title: 'Офис',
    description: 'Данные из анкеты сохранены.',
    icon: <BankOutlined />,
    skippable: false,
  },
  {
    key: 'employees',
    title: 'Добавить сотрудников',
    description:
      'Добавьте сотрудников, которые будут работать в системе: загрузите список из Excel или пригласите по email.',
    icon: <TeamOutlined />,
    menuKeys: ['adminRoles'],
    skippable: true,
  },
  {
    key: 'orders',
    title: 'Добавить заказы',
    description: 'Перенесите заказы из файла Excel. Если импорт не нужен — шаг можно пропустить.',
    icon: <ImportOutlined />,
    menuKeys: ['orders'],
    skippable: true,
  },
  {
    key: 'integrations',
    title: 'Подключить интеграции',
    description: 'Телефония Mango, Авито или Calltouch — подключите хотя бы одну.',
    icon: <ApiOutlined />,
    menuKeys: ['integrations', 'organizations', 'callCenterTelephony'],
    skippable: true,
  },
  {
    key: 'templates',
    title: 'Добавить шаблоны документов',
    description: 'Загрузите шаблоны договоров и других документов.',
    icon: <FileTextOutlined />,
    menuKeys: ['documentTemplates'],
    skippable: true,
  },
]

/** Кнопки шага «Добавить сотрудников»: приглашение по e-mail и импорт из Excel (в модалке). */
function EmployeesActions({
  structure,
  onChanged,
}: {
  structure: CompanyStructure | null
  onChanged: () => void
}) {
  const [inviteOpen, setInviteOpen] = useState(false)
  const importOptions = useMemo(
    () => (structure ? flattenOfficeOptionsForUsers(structure) : []),
    [structure],
  )
  const inviteOptions = useMemo(
    () => (structure ? flattenOfficeOptionsForInvite(structure) : []),
    [structure],
  )

  if (!structure) return <Text type="secondary">Загрузка данных…</Text>
  if (!importOptions.length) {
    return <Text type="secondary">Сначала создайте офис — сотрудников добавляют в офис.</Text>
  }

  return (
    <>
      <Space wrap size={8}>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          disabled={!inviteOptions.length}
          onClick={() => setInviteOpen(true)}
        >
          Пригласить по e-mail
        </Button>
        <ImportUsersBlock variant="modal" officeOptions={importOptions} onImported={onChanged} />
      </Space>
      <InviteEmployeeModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        officeOptions={inviteOptions}
        inviteTtlHours={structure.invite_ttl_hours}
        onInvited={onChanged}
      />
    </>
  )
}

/** Кнопка шага «Добавить заказы»: импорт из Excel (в модалке), как на старом рабочем столе. */
function OrdersActions({
  structure,
  onChanged,
}: {
  structure: CompanyStructure | null
  onChanged: () => void
}) {
  const permissionsWithScope = useSelector(
    (state: any) => state?.session?.sessionData?.permissionsWithScope,
  )
  const canImportOrders = hasPermission(permissionsWithScope, 'admin_orders_import')
  const orderOfficeOptions = useMemo(
    () => (structure ? flattenOfficeOptionsForOrders(structure) : []),
    [structure],
  )

  if (!structure) return <Text type="secondary">Загрузка данных…</Text>
  if (!canImportOrders) {
    return <Text type="secondary">Нет права на импорт заказов (admin_orders_import).</Text>
  }
  if (!orderOfficeOptions.length) {
    return (
      <Text type="secondary">
        Импорт доступен для офисов организаций типа «Дилер» или «Головной офис» — подходящих офисов
        пока нет.
      </Text>
    )
  }

  return (
    <ImportOrdersBlock
      variant="modal"
      buttonType="primary"
      officeOptions={orderOfficeOptions}
      onImported={onChanged}
    />
  )
}
type Props = {
  structure: CompanyStructure | null
  onUsersImported?: () => void
  /** Вызывается после каждой загрузки статуса (например, для приветствия с именем) */
  onStatusLoaded?: (status: OnboardingStatus | null) => void
}

export function HomeOnboardingSteps({ structure, onUsersImported, onStatusLoaded }: Props) {
  const navigate = useNavigate()
  const allowedMenuKeys: string[] | null = useSelector(
    (state: any) => state?.session?.sessionData?.allowedMenuKeys ?? null,
  )
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [savingKey, setSavingKey] = useState<OnboardingStepKey | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchAPI('/api/home/onboarding-status')
      if (res?.errors) {
        setError(res.errors?.[0]?.detail || 'Не удалось загрузить шаги')
        return
      }
      setStatus(res?.data || null)
      onStatusLoaded?.(res?.data || null)
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [onStatusLoaded])

  const handleUsersImported = useCallback(() => {
    onUsersImported?.()
    load()
  }, [load, onUsersImported])

  useEffect(() => {
    load()
  }, [load])

  const setSkipped = useCallback(
    async (key: OnboardingStepKey, skipped: boolean) => {
      setSavingKey(key)
      try {
        const res = await fetchAPI('/api/home/onboarding-status/skip', {
          method: 'POST',
          body: JSON.stringify({ step: key, skipped }),
        })
        if (res?.errors) {
          message.error(res.errors?.[0]?.detail || 'Не удалось сохранить')
          return
        }
        if (res?.data) {
          setStatus(res.data)
          onStatusLoaded?.(res.data)
        }
        setExpanded(prev => ({ ...prev, [key]: !skipped }))
      } catch (e: any) {
        message.error(e?.message || 'Ошибка сохранения')
      } finally {
        setSavingKey(null)
      }
    },
    [onStatusLoaded],
  )

  const visibleSteps = useMemo(
    () =>
      STEPS.filter(step => {
        if (!step.menuKeys || allowedMenuKeys == null) return true
        return step.menuKeys.some(key => allowedMenuKeys.includes(key))
      }),
    [allowedMenuKeys],
  )

  const statusByKey = useMemo(() => {
    const map = new Map<OnboardingStepKey, StepStatus>()
    status?.steps?.forEach(step => map.set(step.key, step))
    return map
  }, [status])

  const currentKey = useMemo(() => {
    const next = visibleSteps.find(step => {
      const st = statusByKey.get(step.key)
      return !st?.done && !st?.skipped
    })
    return next?.key ?? null
  }, [statusByKey, visibleSteps])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    )
  }
  if (error) return <Alert type="error" showIcon message={error} />

  const doneCount = visibleSteps.filter(step => {
    const st = statusByKey.get(step.key)
    return st?.done || st?.skipped
  }).length

  const renderBody = (step: StepMeta) => {
    switch (step.key) {
      case 'office': {
        const office = status?.office
        const poll = status?.poll_summary
        return (
          <Space align="start" size={16}>
            <CheckCircleFilled style={{ fontSize: 48, color: '#52c41a' }} />
            <Space direction="vertical" size={2}>
              <Text strong style={{ fontSize: 16 }}>
                Этап выполнен
              </Text>
              {office?.brand && <Text>Бренд: {office.brand}</Text>}
              {(office?.legal_name || office?.inn) && (
                <Text>
                  Юрлицо: {office?.legal_name || '—'}
                  {office?.inn ? `, ИНН ${office.inn}` : ''}
                </Text>
              )}
              {office?.office && <Text>Офис: {office.office}</Text>}
              {poll?.items?.map(item => (
                <Text key={`${item.question}-${item.answer}`}>
                  {item.question ? `${item.question.replace(/[?:]\s*$/, '')}: ` : ''}
                  {item.answer}
                </Text>
              ))}
              {!!poll?.departments?.length && (
                <div style={{ marginTop: 4 }}>
                  <Text>Подразделения: </Text>
                  {poll.departments.map(dept => (
                    <Tag key={dept} style={{ marginBottom: 4 }}>
                      {dept}
                    </Tag>
                  ))}
                </div>
              )}
            </Space>
          </Space>
        )
      }
      case 'employees': {
        const employees = status?.employees?.items || []
        const total = status?.employees?.total ?? employees.length
        return (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary">{step.description}</Text>
            <EmployeesActions structure={structure} onChanged={handleUsersImported} />
            {employees.length > 0 && (
              <div>
                <Table<EmployeePreview>
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={employees}
                  scroll={{ x: 560 }}
                  columns={[
                    {
                      title: 'ФИО',
                      dataIndex: 'name',
                      render: (_: unknown, row) => (
                        <Link to={`/employees_new/${row.id}`}>{row.name || row.login || '—'}</Link>
                      ),
                    },
                    {
                      title: 'Роль',
                      dataIndex: 'roles',
                      render: (roles: string[]) => (roles?.length ? roles.join(', ') : '—'),
                    },
                    {
                      title: 'Офис',
                      dataIndex: 'office',
                      render: (value: string | null) => value || '—',
                    },
                    {
                      title: 'Активность',
                      dataIndex: 'is_active',
                      width: 130,
                      render: (active: boolean) => (
                        <Badge
                          status={active ? 'success' : 'default'}
                          text={active ? 'Активен' : 'Неактивен'}
                        />
                      ),
                    },
                  ]}
                />
                <div style={{ marginTop: 8 }}>
                  <Link to={ALL_EMPLOYEES_URL}>
                    Все сотрудники{total > employees.length ? ` (${total})` : ''}
                  </Link>
                </div>
              </div>
            )}
            {employees.length === 0 && <Link to={ALL_EMPLOYEES_URL}>Все сотрудники</Link>}
          </Space>
        )
      }
      case 'orders':
        return (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary">{step.description}</Text>
            <OrdersActions structure={structure} onChanged={load} />
          </Space>
        )
      case 'integrations':
        return (
          <Space direction="vertical" size={12}>
            <Text type="secondary">{step.description}</Text>
            <HomeIntegrationsConnect onChanged={load} />
          </Space>
        )
      case 'templates':
        return (
          <Space direction="vertical" size={12}>
            <Text type="secondary">{step.description}</Text>
            <Button type="primary" onClick={() => navigate('/document-templates')}>
              Перейти к шаблонам
            </Button>
          </Space>
        )
      default:
        return null
    }
  }

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Text type="secondary">
        Выполнено {doneCount} из {visibleSteps.length}
      </Text>
      {visibleSteps.map((step, index) => {
        const st = statusByKey.get(step.key)
        const isDone = Boolean(st?.done)
        const isSkipped = !isDone && Boolean(st?.skipped)
        const isCurrent = step.key === currentKey
        const collapsible = isDone || isSkipped
        const isOpen = collapsible ? Boolean(expanded[step.key]) : true
        const toggle = () =>
          collapsible && setExpanded(prev => ({ ...prev, [step.key]: !prev[step.key] }))

        return (
          <Card
            key={step.key}
            size="small"
            data-step={step.key}
            style={{
              borderColor: isCurrent ? '#1677ff' : undefined,
              boxShadow: isCurrent ? '0 0 0 2px rgba(22,119,255,0.15)' : undefined,
              opacity: !isCurrent && !collapsible ? 0.85 : 1,
            }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <div
              onClick={toggle}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: collapsible ? 'pointer' : 'default',
                marginBottom: isOpen ? 12 : 0,
              }}
            >
              {isDone ? (
                <CheckCircleFilled style={{ fontSize: 22, color: '#52c41a' }} />
              ) : (
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    color: isCurrent ? '#fff' : '#8c8c8c',
                    background: isCurrent ? '#1677ff' : '#f0f0f0',
                  }}
                >
                  {index + 1}
                </span>
              )}
              <span style={{ color: '#8c8c8c' }}>{step.icon}</span>
              <Title level={5} style={{ margin: 0, flex: 1 }}>
                {step.title}
              </Title>
              {isCurrent && <Tag color="blue">Следующий шаг</Tag>}
              {isSkipped && <Tag>пропущено</Tag>}
              {collapsible && (isOpen ? <DownOutlined /> : <RightOutlined />)}
            </div>

            {isOpen && (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {renderBody(step)}
                {step.skippable && !isDone && (
                  <div>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      loading={savingKey === step.key}
                      onClick={() => setSkipped(step.key, !isSkipped)}
                    >
                      {isSkipped ? 'Вернуть шаг' : 'Пропустить'}
                    </Button>
                  </div>
                )}
              </Space>
            )}
          </Card>
        )
      })}
    </Space>
  )
}

export default HomeOnboardingSteps
